import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import { BodyText, Button, Card, Pagination } from '../ui';
import { DietitianCard } from './DietitianCard';
import { cn } from '../../utils/cn';
import type { DietitianProfile } from '../../types/referral';

interface DietitianListProps {
  dietitians: DietitianProfile[];
  selectedDietitian: DietitianProfile | null;
  onDietitianSelect: (dietitian: DietitianProfile) => void;
  onClearFilters: () => void;
  isLoading?: boolean;
  isFiltered?: boolean;
  // Pagination props
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
}

/**
 * Container for dietitian cards with filtering logic
 * Handles loading states, empty states, and dietitian selection
 */
export const DietitianList: React.FC<DietitianListProps> = ({
  dietitians,
  selectedDietitian,
  onDietitianSelect,
  onClearFilters,
  isLoading = false,
  isFiltered = false,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 5,
  onPageChange,
}) => {
  const { isDark } = useTheme();

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Loading skeleton cards */}
        {[1, 2, 3, 4].map((index) => (
          <Card
            key={index}
            className={cn(
              'p-6 animate-pulse',
              isDark ? 'bg-secondary-800' : 'bg-white'
            )}
          >
            <div className="flex items-start space-x-4">
              {/* Avatar skeleton */}
              <div className={cn(
                'w-16 h-16 rounded-full',
                isDark ? 'bg-secondary-700' : 'bg-secondary-200'
              )} />

              {/* Content skeleton */}
              <div className="flex-1 space-y-2">
                <div className={cn(
                  'h-4 rounded w-1/3',
                  isDark ? 'bg-secondary-700' : 'bg-secondary-200'
                )} />
                <div className={cn(
                  'h-3 rounded w-1/4',
                  isDark ? 'bg-secondary-700' : 'bg-secondary-200'
                )} />
                <div className={cn(
                  'h-3 rounded w-1/2',
                  isDark ? 'bg-secondary-700' : 'bg-secondary-200'
                )} />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  // No results state
  if (dietitians.length === 0) {
    return (
      <Card className={cn(
        'p-8 text-center',
        isDark ? 'bg-secondary-800' : 'bg-white'
      )}>
        <div className="max-w-md mx-auto">
          <div className="text-4xl mb-4">🔍</div>
          <BodyText variant="primary" className="mb-2 font-medium">
            {isFiltered ? 'No dietitians match your criteria' : 'No dietitians available'}
          </BodyText>
          <BodyText variant="secondary" className="mb-6">
            {isFiltered
              ? 'Try adjusting your search terms or filters to find more options.'
              : 'There are currently no dietitians available in our system.'
            }
          </BodyText>
          {isFiltered && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClearFilters}
              className="mx-auto"
            >
              Clear All Filters
            </Button>
          )}
        </div>
      </Card>
    );
  }

  // Results header
  const resultsHeader = (
    <div className="mb-6 flex justify-between items-center">
      <BodyText variant="secondary">
        {totalItems > 0 ? (
          <>
            {totalItems} dietitian{totalItems !== 1 ? 's' : ''} available
            {isFiltered && ' (filtered)'}
            {totalPages > 1 && (
              <span className="ml-2">
                • Page {currentPage} of {totalPages}
              </span>
            )}
          </>
        ) : (
          <>
            {dietitians.length} dietitian{dietitians.length !== 1 ? 's' : ''} available
            {isFiltered && ' (filtered)'}
          </>
        )}
      </BodyText>
      {selectedDietitian && (
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 rounded-full bg-success-500" />
          <BodyText variant="primary" size="sm" className="font-medium">
            {selectedDietitian.name} selected
          </BodyText>
        </div>
      )}
    </div>
  );

  // Dietitian cards grid
  const dietitianGrid = (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-6">
      {dietitians.map((dietitian) => (
        <DietitianCard
          key={dietitian.id}
          dietitian={dietitian}
          isSelected={selectedDietitian?.id === dietitian.id}
          onSelect={onDietitianSelect}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {resultsHeader}
      {dietitianGrid}

      {/* Pagination */}
      {totalPages > 1 && onPageChange && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
          showInfo={false}
          showPageNumbers={true}
          size="sm"
          className="mt-6"
        />
      )}
    </div>
  );
};